import { useEffect, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import {
  useDeleteGraduationClassMutation,
  useGetGraduationClassesByProgramLazyQuery,
} from "graphql/__generated__/hooks";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { Link, useNavigate } from "react-router-dom";
import { GraduationClass } from "types/graduationClass";
import { useProgramOrganization } from "hooks/useProgramOrganization";
import { ResponsiveButtonLink } from "components/Button";
import { useNotifications } from "hooks/useNotifications";
import { Link as LinkBase, Stack } from "@mui/material";
import { useClinicText } from "hooks/useClinicText";
import { AdminRoutes } from "config/routes";

export const GraduationClassList = () => {
  const { organizationId, program } = useProgramOrganization();
  const navigate = useNavigate();
  const [deleteGraduationClassOpen, setDeleteGraduationClassOpen] =
    useState(false);
  const [academicYearIdToDelete, setAcademicYearIdToDelete] = useState("");
  const [graduationClass, setGraduationClass] = useState(
    [] as GraduationClass[],
  );
  const { textGraduationClasses, textGraduationClass } = useClinicText();

  const [deleteGraduationClass] = useDeleteGraduationClassMutation();
  const [getGraduationClasses, { loading, error, data, refetch }] =
    useGetGraduationClassesByProgramLazyQuery();
  const { notify } = useNotifications();

  useEffect(() => {
    if (data?.graduation_class) {
      setGraduationClass((data?.graduation_class || []) as GraduationClass[]);
    }
  }, [data]);

  useEffect(() => {
    if (program?.program_id) {
      getGraduationClasses({ variables: { programId: program?.program_id } });
    }
  }, [program]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.VIEW:
        navigate(`/organization/${organizationId}/graduation-class/${id}`);
        break;
      case TABLE_ACTIONS.EDIT:
        navigate(`/organization/${organizationId}/graduation-class/${id}/edit`);
        break;
      case TABLE_ACTIONS.DELETE:
        setAcademicYearIdToDelete(id);
        setDeleteGraduationClassOpen(true);
        break;
      default:
        break;
    }
  };

  const handleDelete = async () => {
    if (!academicYearIdToDelete || academicYearIdToDelete === "") {
      return;
    }
    await deleteGraduationClass({ variables: { id: academicYearIdToDelete } });
    await refetch();
    setAcademicYearIdToDelete("");
    notify({
      message: Messages.successGraduationClassDeleted,
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title={textGraduationClasses}
      loading={loading}
      error={error}
      buttonActions={
        <Stack direction="row" spacing={2}>
          <ResponsiveButtonLink
            to={AdminRoutes.UploadCSVCohorts}
            variant="outlined"
            icon={<FileUploadIcon />}
          >
            Upload CSV File
          </ResponsiveButtonLink>
          <ResponsiveButtonLink
            to={`/organization/${organizationId}/graduation-class/create/`}
            icon={<Add />}
          >
            New {textGraduationClass}
          </ResponsiveButtonLink>
        </Stack>
      }
    >
      <CustomTable
        entities={graduationClass}
        handleActionClick={handleActionClick}
        fieldToFilter={"name"}
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "graduation_class_id",
            value: (row: any) => (
              <Stack direction="row" spacing={2} alignItems={"center"}>
                <LinkBase
                  sx={{ cursor: "pointer" }}
                  component={Link}
                  to={`/organization/${organizationId}/graduation-class/${row.graduation_class_id}`}
                >
                  View
                </LinkBase>
              </Stack>
            ),
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[
          TABLE_ACTIONS.VIEW,
          TABLE_ACTIONS.EDIT,
          TABLE_ACTIONS.DELETE,
        ]}
        extraActionItems={[]}
        idColumn={"graduation_class_id"}
      />

      {deleteGraduationClassOpen && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={`Are you sure you want to delete this ${textGraduationClass}?`}
          show={deleteGraduationClassOpen}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteGraduationClassOpen(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
};
